import type React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from './AuthContext';

/**
 * Gates a route on an authenticated admin.
 *
 * Also mirrors the server's requirePasswordChanged gate: while
 * mustChangePassword is set, every route except /change-password is redirected
 * there — so the UI never renders a screen whose API calls would 403.
 */
export function RequireAuth({ children }: { children: ReactNode }): React.JSX.Element {
  const { admin, initializing } = useAuth();
  const location = useLocation();

  if (initializing) {
    return <div className="page-loading">Carregando…</div>;
  }

  if (!admin) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (admin.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  return <>{children}</>;
}

/**
 * Additionally requires the super_admin role.
 *
 * A convenience for the UI only — the server enforces this independently on
 * every /v1/admin/admins route, so hiding the page is never the actual control.
 */
export function RequireSuperAdmin({ children }: { children: ReactNode }): React.JSX.Element {
  const { admin, initializing } = useAuth();

  if (initializing) {
    return <div className="page-loading">Carregando…</div>;
  }
  if (admin?.role !== 'super_admin') {
    return (
      <div className="empty-state">
        <h2>Acesso restrito</h2>
        <p>Somente super administradores podem gerenciar contas de administrador.</p>
      </div>
    );
  }
  return <>{children}</>;
}
